import React from 'react';
import styled from 'styled-components';
import { gemstoneDetails } from '../data/gemstoneData';
import { Link } from 'react-router-dom';

const Section = styled.section`
  padding: ${props => props.theme.spacing.section} 40px;
  max-width: ${props => props.theme.spacing.container};
  margin: 0 auto;
  background: ${props => props.theme.colors.backgroundAlt};
`;

const Title = styled.h2`
  font-size: 42px;
  text-align: center;
  margin-bottom: 20px;
  color: ${props => props.theme.colors.primary};
  font-weight: 400;
  letter-spacing: 1px;
  position: relative;
  display: inline-block;
  width: 100%;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, ${props => props.theme.colors.secondary}, ${props => props.theme.colors.accent});
    margin: 20px auto 0;
    border-radius: 2px;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 60px;
  max-width: 650px;
  margin-left: auto;
  margin-right: auto;
  font-size: 17px;
  line-height: 1.8;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const GemstoneCard = styled(Link)`
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.large};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.medium};
  transition: all ${props => props.theme.transitions.medium};
  text-decoration: none;
  display: block;
  
  &:hover {
    transform: translateY(-12px);
    box-shadow: ${props => props.theme.shadows.hover};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(to top, rgba(255,255,255,0.8), transparent);
    pointer-events: none;
  }
`;

const GemImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: contain;
  object-position: center;
  transition: transform ${props => props.theme.transitions.slow};
  
  ${GemstoneCard}:hover & {
    transform: scale(1.05);
  }
`;

const GemInfo = styled.div`
  padding: 30px 35px;
`;

const GemName = styled.h3`
  font-size: 28px;
  margin-bottom: 20px;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  position: relative;
  padding-bottom: 15px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background: ${props => props.theme.colors.secondary};
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;
  background: ${props => props.theme.colors.backgroundAlt};
  padding: 18px;
  border-radius: ${props => props.theme.borderRadius.medium};
`;

const DetailItem = styled.div`
  font-size: 14px;
  
  strong {
    color: ${props => props.theme.colors.secondary};
    font-weight: 600;
    display: block;
    margin-bottom: 4px;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.8px;
  }
  
  span {
    color: ${props => props.theme.colors.text};
    font-size: 15px;
    font-weight: 500;
  }
`;

const Description = styled.p`
  font-size: 15px;
  line-height: 1.8;
  color: ${props => props.theme.colors.textLight};
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const ViewButton = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: 15px;
  color: ${props => props.theme.colors.secondary};
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:after {
    content: '→';
    margin-left: 8px;
    transition: transform ${props => props.theme.transitions.fast};
  }
  
  ${GemstoneCard}:hover &:after {
    transform: translateX(5px);
  }
`;

const GemstoneGrid = () => {
  const featuredGems = gemstoneDetails.filter(gem => 
    gem.name === "Ruby" || gem.name === "Emerald"
  );

  if (featuredGems.length === 0) {
    return (
      <Section>
        <Title>Featured Gemstones</Title>
        <Subtitle>Coming soon...</Subtitle>
      </Section>
    );
  }

  return (
    <Section>
      <Title>Featured Gemstones</Title>
      <Subtitle>
        Discover the timeless elegance of our most treasured stones - Ruby and Emerald,
        each with their unique history, exceptional beauty, and remarkable characteristics
      </Subtitle>
      <Grid>
        {featuredGems.map((gem) => (
          <GemstoneCard key={gem.id} to={`/gem/${gem.name.toLowerCase().replace(' ', '-')}`}>
            <ImageContainer>
              <GemImage src={gem.image} alt={gem.name} />
            </ImageContainer>
            <GemInfo>
              <GemName>{gem.name}</GemName>
              <DetailGrid>
                <DetailItem>
                  <strong>Hardness</strong>
                  <span>{gem.hardness || "N/A"}</span>
                </DetailItem>
                <DetailItem>
                  <strong>Color</strong>
                  <span>{gem.color || "N/A"}</span>
                </DetailItem>
                <DetailItem>
                  <strong>Birthstone</strong>
                  <span>{gem.birthstone || "N/A"}</span>
                </DetailItem>
              </DetailGrid>
              <Description>{gem.description || "Detailed information coming soon."}</Description>
              <ViewButton>Explore</ViewButton>
            </GemInfo>
          </GemstoneCard>
        ))}
      </Grid>
    </Section>
  );
};

export default GemstoneGrid;
